import * as React from "react";
import { IconButton, List as MList } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import ActivityDetailWithoutLink from "../Dashboard/ActivityDetailWithoutLink";
import ThriveTitle from "./ThriveTitle";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { StyledButton, StyledButtonMain } from "./Buttons";
import SearchIcon from "@mui/icons-material/Search";
import ActivityDetail from "../Dashboard/ActivityDetail";
import { FixedSizeList as List } from "react-window";
import { axiosInstance, fetchInstance } from "../../utils/networking";
import { UserContext } from "../../context/UserContext";
import { UserType } from "../../interfaces";
import { useLocation } from "react-router-dom";
import { filterActiveUsers } from "../Dashboard/ProgramStatistics/ProgramStatistics";
interface Props {
  title: string;
  cases: any[];
  checkedCases: string[];
  setCheckedCases: any;
  selectedCase: any;
  setSelectedCase: any;
  enableControl: boolean;
  enableSelection: boolean;
  enableMultiSelection: boolean;
  status_default: string;
  sortby_default: string;
  period_default: string; // Optional prop for period default
  setOpen: any;
  handleNext: any;
  height: number | string;
  setEventEmailTos?: any; // Optional prop for event email tos
  reloadCases: boolean; // Optional prop to reload cases
}

// How many cases to pull per request. The list is virtualized, so this only
// needs to comfortably overfill the viewport; more pages load on scroll.
const PAGE_SIZE = 60;

interface RowData {
  cases: any[];
  checkedSet: Set<string>;
  checkedCases: string[];
  selectedCase: any;
  enableSelection: boolean;
  enableMultiSelection: boolean;
  statusMap: { [key: string]: string };
  onToggle: (id: string) => () => void;
  onSelect: (caseItem: any) => void;
}

// Defined at module scope, not inside the component. When this lived in the
// component body React saw a brand new component type on every render, so
// react-window unmounted and remounted every visible row on each keystroke.
const Row: React.FC<{
  index: number;
  style: React.CSSProperties;
  data: RowData;
}> = ({ index, style, data }) => {
  const caseItem = data.cases[index];
  if (!caseItem) {
    return <ListItem disablePadding sx={style} />;
  }
  const caseId = caseItem["_id"];
  const labelId = `checkbox-list-secondary-label-${caseId}`;
  const firm = caseItem["firmRecord"];

  return (
    <ListItem disablePadding sx={style}>
      {data.enableSelection && data.enableMultiSelection ? (
        <Checkbox
          edge="end"
          onChange={data.onToggle(caseId)}
          checked={data.checkedSet.has(caseId)}
          inputProps={{ "aria-labelledby": labelId }}
        />
      ) : (
        <></>
      )}

      <ListItemButton>
        {data.enableSelection ? (
          <ActivityDetailWithoutLink
            key={labelId}
            case={caseItem}
            firm={firm}
            selectedCase={
              data.enableMultiSelection ? data.checkedCases : data.selectedCase
            }
            onSelect={
              data.enableMultiSelection
                ? data.onToggle(caseId)
                : () => data.onSelect(caseItem)
            }
            index={firm["_id"]}
            index2={index}
            multiselect={data.enableMultiSelection}
          />
        ) : (
          <ActivityDetail
            key={labelId}
            case={caseItem}
            showStatus={data.statusMap[caseItem.status]}
            firm={firm}
          />
        )}
      </ListItemButton>
    </ListItem>
  );
};
const MemoRow = React.memo(Row);

const ThriveCaseSelection: React.FC<Props> = ({
  title,
  cases,
  checkedCases,
  setCheckedCases,
  selectedCase,
  setSelectedCase,
  enableControl,
  enableSelection,
  enableMultiSelection,
  status_default,
  sortby_default,
  period_default,
  setOpen,
  handleNext,
  height,
  setEventEmailTos,
  reloadCases,
}: Props) => {
  const location = useLocation();

  const statusMap: { [key: string]: string } = {
    open: "Open",
    completed: "Closed",
    either: "Either",
  };
  const [currentCases, setCurrentCases] = React.useState<any[]>(cases);
  const [sortby, setSortby] = React.useState<string>(sortby_default);
  const [status, setStatus] = React.useState<string>(status_default);
  const [period, setPeriod] = React.useState<string>(period_default);
  const [primaryIndustry, setPrimaryIndustry] = React.useState<string>("all");
  const [sbc, setSbc] = React.useState<string>("all");
  const [search, setSearch] = React.useState<string>("");
  // `search` is the text in the box; `appliedSearch` is the term the server is
  // actually filtering on. Split so typing does not fire a request per
  // keystroke — the term is submitted on Enter or the search button.
  const [appliedSearch, setAppliedSearch] = React.useState<string>("");
  const [loadingFlag, setLoadingFlag] = React.useState<boolean>(true);
  const [loadingMore, setLoadingMore] = React.useState<boolean>(false);
  const [hasMore, setHasMore] = React.useState<boolean>(true);
  const [searchFlag, setSearchFlag] = React.useState<boolean>(false);
  const [exporting, setExporting] = React.useState<boolean>(false);
  // const fullyChecked = Array.from(Array(currentCases.length).keys());
  const { user } = React.useContext(UserContext);
  const [availableUsers, setAvailableUsers] = React.useState<any[]>([]);

  // Sync currentCases with the cases prop when it changes, unless loading or search is active
  React.useEffect(() => {
    if (!reloadCases) {
      setCurrentCases(cases);
    }
  }, [cases]);

  // Tracks the in-flight request so a superseded one can be aborted and its
  // (stale) result discarded — previously two rapid filter changes raced and
  // whichever landed last won.
  const requestRef = React.useRef<AbortController | null>(null);
  const skipRef = React.useRef<number>(0);
  // case_id -> firm_id for every case seen this session. `currentCases` only
  // holds the currently loaded page/search, so it cannot resolve a case the
  // user checked on an earlier page or before searching — which would silently
  // drop event-invitation recipients.
  const caseFirmRef = React.useRef<Map<string, string>>(new Map());

  const rememberFirms = React.useCallback((rows: any[]) => {
    for (const c of rows) {
      const firmId = c?.firmRecord?._id;
      if (c?._id && firmId) caseFirmRef.current.set(String(c._id), firmId);
    }
  }, []);

  const firmsForChecked = React.useCallback(
    (ids: string[]) =>
      ids
        .map((id) => caseFirmRef.current.get(String(id)))
        .filter((f): f is string => Boolean(f)),
    []
  );

  const fetchPage = React.useCallback(
    async (append: boolean, term: string = appliedSearch) => {
      requestRef.current?.abort();
      const controller = new AbortController();
      requestRef.current = controller;

      const skip = append ? skipRef.current : 0;
      if (append) setLoadingMore(true);
      else {
        setLoadingFlag(true);
        setHasMore(true);
      }

      try {
        const response = await fetchInstance(
          `${process.env.REACT_APP_API_DOMAIN}/cases/recent`,
          {
            method: "POST",
            body: JSON.stringify({
              period: period,
              caseStatus: status,
              sortBy: sortby,
              primaryIndustry: primaryIndustry,
              ids: [],
              sbc: sbc,
              skip,
              limit: PAGE_SIZE,
              searchTerm: term,
            }),
            signal: controller.signal,
          }
        );
        if (!response.body) {
          throw new Error("ReadableStream not supported in this environment.");
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";
        let stream_cases: any[] = [];

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || ""; // keep incomplete line

          for (const line of lines) {
            if (line.trim()) {
              try {
                stream_cases.push(JSON.parse(line));
              } catch (err) {
                console.error("Failed to parse line:", line, err);
              }
            }
          }
        }

        if (controller.signal.aborted) return;

        // A short page means the server has nothing more to give.
        const received = stream_cases.length;
        skipRef.current = skip + received;
        if (received < PAGE_SIZE) setHasMore(false);

        //Filter out the current case if we are on the case page to avoid merging with itself
        if (location.pathname === "/case") {
          const currentCaseId = location.search.split("id=")[1];
          stream_cases = stream_cases.filter((c) => c._id !== currentCaseId);
        }

        rememberFirms(stream_cases);

        // Append rather than replace so the sort order the server produced
        // (latest activity first) is preserved across pages.
        setCurrentCases((prev) =>
          append ? [...prev, ...stream_cases] : stream_cases
        );
      } catch (err: any) {
        if (err?.name !== "AbortError") {
          console.error("Failed to load cases:", err);
        }
        return;
      } finally {
        if (!controller.signal.aborted) {
          setLoadingFlag(false);
          setLoadingMore(false);
        }
      }
    },
    [
      period,
      status,
      sortby,
      primaryIndustry,
      sbc,
      location,
      appliedSearch,
      rememberFirms,
    ]
  );

  React.useEffect(() => {
    if (!reloadCases) {
      setLoadingFlag(false);
      return;
    }
    setCurrentCases([]);
    skipRef.current = 0;
    fetchPage(false, appliedSearch);
    return () => requestRef.current?.abort();
    // `fetchPage` is deliberately omitted: it closes over `location`, whose
    // identity changes on every navigation, which would restart the fetch in a
    // loop. The filter values it actually depends on are all listed here —
    // including `appliedSearch`, since the server now does the searching.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    status,
    sortby,
    period,
    primaryIndustry,
    reloadCases,
    sbc,
    appliedSearch,
  ]);

  // react-window tells us which rows are mounted; pull the next page as the
  // user approaches the end of what we have. Search results page the same way
  // as unfiltered ones, because the server applies the search term.
  const handleItemsRendered = React.useCallback(
    ({ visibleStopIndex }: { visibleStopIndex: number }) => {
      if (
        hasMore &&
        !loadingMore &&
        !loadingFlag &&
        visibleStopIndex >= currentCases.length - 10
      ) {
        fetchPage(true);
      }
    },
    [hasMore, loadingMore, loadingFlag, currentCases.length, fetchPage]
  );

  React.useEffect(() => {
    if (user.role === "admin") {
      axiosInstance.get(`/user/allUsers`).then((res) => {
        const users = filterActiveUsers(
          res.data.users as UserType[]
        ) as UserType[];
        const userNames = users.map((user: any) => ({
          label: user.firstName + " " + user.lastName,
          id: user._id,
        }));
        setAvailableUsers(userNames);
      });
    }
  }, []);

  const handleToggle = (value: string) => () => {
    const currentIndex = checkedCases.indexOf(value);
    const newChecked = [...checkedCases];
    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setCheckedCases(newChecked);
    if (setEventEmailTos) {
      setEventEmailTos(firmsForChecked(newChecked));
    }
  };

  const handleToggleAll = () => {
    const visibleIds = currentCases.map((c) => c._id);
    const visibleSelectedCount = visibleIds.filter((id) =>
      checkedCases.includes(id)
    ).length;

    let newChecked: string[];

    if (visibleSelectedCount < visibleIds.length) {
      // Select all visible (union)
      const existingHidden = checkedCases.filter(
        (id) => !visibleIds.includes(id)
      );
      newChecked = [...existingHidden, ...visibleIds];
    } else {
      // Deselect all visible
      newChecked = checkedCases.filter((id) => !visibleIds.includes(id));
    }
    setCheckedCases(newChecked);

    if (setEventEmailTos) {
      setEventEmailTos(firmsForChecked(newChecked));
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  // The list is paged, so `currentCases` only holds what has been scrolled
  // into view. Export must cover the whole filtered result set, so it asks the
  // server for an unpaged copy (limit omitted) using the active filters and
  // search term.
  const fetchAllForExport = async (): Promise<any[]> => {
    const response = await fetchInstance(
      `${process.env.REACT_APP_API_DOMAIN}/cases/recent`,
      {
        method: "POST",
        body: JSON.stringify({
          period: period,
          caseStatus: status,
          sortBy: sortby,
          primaryIndustry: primaryIndustry,
          ids: [],
          sbc: sbc,
          searchTerm: appliedSearch,
        }),
      }
    );
    const text = await response.text();
    return text
      .split("\n")
      .filter((l) => l.trim())
      .map((l) => JSON.parse(l));
  };

  const handleExport = async () => {
    setExporting(true);
    let rows: any[];
    try {
      rows = await fetchAllForExport();
    } catch (err) {
      console.error("Export failed:", err);
      setExporting(false);
      return;
    }
    let csvContent: any[] = [];
    const csvHeading = [
      "thriveID",
      "Company Name",
      "Contact",
      "Contact First Name",
      "Contact Last Name",
      "Email",
      "Phone",
      "Website",
      "Address",
      "City",
      "ZIP",
      "Founding Year",
      "Company Narrative",
      "primaryIndustry",
      "secondaryIndustry",
      "Annual Revenue",
      "Employees",
      "Woman-Owned",
      "Veteran-Owned",
      "Minority-Owned",
      "firmScore",
      "totalGrantsValue",
      "Home-based",
      "Selling Online",
      "In-Person Sales",
      "Commercial Location",
      "Mobile Business",
      "Case Status",
      "Created Date",
      "Last Activity Date",
      "Primary SBC",
    ];
    csvContent.push(csvHeading);

    for (const c of rows) {
      const firm = c.firmRecord ?? {};
      const contact = firm["Contact"] ? String(firm["Contact"]) : "";
      const hasSpace = contact.includes(" ");
      const firstName = hasSpace ? contact.split(" ")[0] : contact;
      const lastName = hasSpace ? contact.split(" ").slice(-1)[0] : contact;
      const csvRow = [];
      for (const key of csvHeading) {
        if (key === "thriveID") csvRow.push(firm["_id"] ?? "");
        else if (key === "Contact First Name") csvRow.push(firstName);
        else if (key === "Contact Last Name") csvRow.push(lastName);
        else if (key === "Case Status") csvRow.push(statusMap[c.status] ?? c.status);
        else if (key === "Created Date") csvRow.push(c.date ? new Date(c.date).toLocaleDateString() : "");
        else if (key === "Last Activity Date") csvRow.push(c.lastActivityDate ? new Date(c.lastActivityDate).toLocaleDateString() : "");
        else if (key === "Primary SBC") csvRow.push(c.primarySbc ?? c.createdBy ?? "");
        else csvRow.push(firm[key] ?? "");
      }
      csvContent.push(csvRow);
    }

    let csvString = ``;
    for (const row of csvContent) {
      let rowString = `\n`;
      for (const item of row) {
        rowString = rowString + `"${item}",`;
      }
      csvString = csvString + rowString;
    }

    const blob = new Blob([csvString], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "cases_export.csv";
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    axiosInstance.post("/export_log", {
      export_type: "cases",
      record_count: rows.length,
      filters: { status, period, primaryIndustry, sbc, search: appliedSearch },
    });
    setExporting(false);
  };

  // Submitting the term re-runs the query server-side, so it matches against
  // every case the user can see rather than only the pages already loaded.
  // The refetch is driven by the `appliedSearch` effect dependency.
  const handleSearch = () => {
    const term = search.trim();
    if (term === appliedSearch) return;
    setSearchFlag(term !== "");
    setAppliedSearch(term);
  };

  const handleClearSearch = () => {
    setSearch("");
    setSearchFlag(false);
    setAppliedSearch("");
  };

  // O(1) membership tests for the row renderer and the select-all checkbox,
  // instead of an O(n) indexOf/includes per row per render.
  const checkedSet = React.useMemo(
    () => new Set(checkedCases),
    [checkedCases]
  );

  const allVisibleChecked =
    currentCases.length > 0 && currentCases.every((c) => checkedSet.has(c._id));

  const rowData: RowData = React.useMemo(
    () => ({
      cases: currentCases,
      checkedSet,
      checkedCases,
      selectedCase,
      enableSelection,
      enableMultiSelection,
      statusMap,
      onToggle: handleToggle,
      onSelect: setSelectedCase,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentCases,
      checkedSet,
      checkedCases,
      selectedCase,
      enableSelection,
      enableMultiSelection,
    ]
  );

  // console.log("Current Cases: ", currentCases);
  // console.log("Available Users: ", availableUsers);
  return (
    <>
      {title ? (
        <ThriveTitle title={title} setOpen={setOpen}></ThriveTitle>
      ) : (
        <></>
      )}

      <MList
        dense
        sx={{
          width: "100%",
          margin: "auto",
          bgcolor: "white",
          overflow: "auto",
          // height: "85%",
        }}
      >
        {enableControl ? (
          <ListItem>
            <Stack direction={"row"} spacing={1}>
              <FormControl sx={{ width: "100px" }}>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  value={status}
                  label="Status"
                  onChange={(e) => {
                    setStatus(e.target.value);
                  }}
                >
                  <MenuItem value={"either"}>Either</MenuItem>
                  <MenuItem value={"open"}>Open</MenuItem>
                  <MenuItem value={"completed"}>Closed</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ width: "100px" }}>
                <InputLabel id="sort-label">Sort By</InputLabel>
                <Select
                  labelId="sort-label"
                  value={sortby}
                  label="Sort By"
                  onChange={(e: any) => {
                    setSortby(e.target.value);
                  }}
                >
                  <MenuItem value={"recent"}>Recent</MenuItem>
                  <MenuItem value={"name"}>Name</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ width: "200px" }}>
                <InputLabel id="period-label">Period</InputLabel>
                <Select
                  labelId="period-label"
                  value={period}
                  label="Period"
                  onChange={(e: any) => {
                    setPeriod(e.target.value);
                  }}
                >
                  <MenuItem value={"1d"}>1 Day</MenuItem>
                  <MenuItem value={"1w"}>1 Week</MenuItem>
                  <MenuItem value={"2w"}>2 Weeks</MenuItem>
                  <MenuItem value={"1m"}>1 Month</MenuItem>
                  <MenuItem value={"2m"}>2 Months</MenuItem>
                  <MenuItem value={"all"}>All</MenuItem>
                </Select>
              </FormControl>
              <TextField
                sx={{ width: "300px" }}
                label="Search Company..."
                variant="outlined"
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                value={search}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label="search"
                      edge="end"
                      onClick={handleSearch}
                    >
                      <SearchIcon />
                    </IconButton>
                  ),
                }}
              />
              {searchFlag ? (
                <StyledButtonMain
                  variant="contained"
                  color="primary"
                  onClick={handleClearSearch}
                >
                  Clear Search
                </StyledButtonMain>
              ) : (
                <></>
              )}
            </Stack>
          </ListItem>
        ) : (
          <> </>
        )}

        {enableControl ? (
          <ListItem>
            <Stack direction={"row"} spacing={1}>
              <FormControl sx={{ width: "300px" }}>
                <InputLabel id="primary-industry-label">
                  Primary Industry
                </InputLabel>
                <Select
                  labelId="primary-industry-label"
                  value={primaryIndustry}
                  label="Primary Industry"
                  onChange={(e) => {
                    setPrimaryIndustry(e.target.value);
                  }}
                >
                  <MenuItem value={"all"}>All</MenuItem>
                  <MenuItem
                    value={
                      "Accommodations - Bed and breakfast, or any other lodging facility"
                    }
                  >
                    Accommodations - Bed and breakfast, or any other lodging
                    facility
                  </MenuItem>
                  <MenuItem value={"Business Services"}>
                    Business Services
                  </MenuItem>
                  <MenuItem
                    value={
                      "Construction and Maintenance Services - Grading, excavating, other construction and maintenance services"
                    }
                  >
                    Construction and Maintenance Services - Grading, excavating,
                    other construction and maintenance services
                  </MenuItem>
                  <MenuItem value={"Engineering, Design and Development"}>
                    Engineering, Design and Development
                  </MenuItem>
                  <MenuItem value={"Entertainment and Recreation"}>
                    Entertainment and Recreation
                  </MenuItem>
                  <MenuItem value={"Food"}>Food</MenuItem>
                  <MenuItem
                    value={"Healthcare - chiropractors, dental, health clinics"}
                  >
                    Healthcare - chiropractors, dental, health clinics
                  </MenuItem>
                  <MenuItem
                    value={
                      "Maintenance & Repair - automobile repair, automobile dealership, cellular phone repair, landscaping and lawn maintenance"
                    }
                  >
                    Maintenance & Repair - automobile repair, automobile
                    dealership, cellular phone repair, landscaping and lawn
                    maintenance
                  </MenuItem>
                  <MenuItem
                    value={
                      "Nonprofit - churches, religious centers, advocacy organizations, etc."
                    }
                  >
                    Nonprofit - churches, religious centers, advocacy
                    organizations, etc.
                  </MenuItem>
                  <MenuItem value={"Other"}>Other</MenuItem>
                  <MenuItem value={"Manufacturing"}>Manufacturing</MenuItem>
                  <MenuItem value={"Personal Services"}>
                    Personal Services
                  </MenuItem>
                  <MenuItem
                    value={
                      "Professional & Other Services - taxes, consulting, publishing, marketing"
                    }
                  >
                    Professional & Other Services - taxes, consulting,
                    publishing, marketing
                  </MenuItem>
                  <MenuItem value={"Retail"}>Retail</MenuItem>
                  <MenuItem value={"Technology"}>Technology</MenuItem>
                  <MenuItem value={"Transportation - limousine services, etc."}>
                    Transportation - limousine services, etc.
                  </MenuItem>
                </Select>
              </FormControl>
              {user.role === "admin" && (
                <FormControl sx={{ width: "200px" }}>
                  <InputLabel id="sbc-label">SBC</InputLabel>
                  <Select
                    labelId="sbc-label"
                    value={sbc}
                    label="SBC"
                    onChange={(e: any) => {
                      setSbc(e.target.value);
                    }}
                  >
                    <MenuItem value={"all"}>All</MenuItem>
                    {availableUsers.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <IconButton
                onClick={handleExport}
                title="Export to CSV"
                disabled={currentCases.length === 0 || exporting}
              >
                <FileDownloadIcon />
              </IconButton>
            </Stack>
          </ListItem>
        ) : (
          <> </>
        )}

        <ListItem disablePadding>
          {enableSelection && enableMultiSelection ? (
            <>
              <Checkbox
                edge="end"
                onChange={handleToggleAll}
                indeterminate={
                  currentCases.some((c) => checkedSet.has(c._id)) &&
                  !currentCases.every((c) => checkedSet.has(c._id))
                }
                checked={allVisibleChecked}
              />
              <ListItemButton>
                {allVisibleChecked ? "DESELECT ALL" : "SELECT ALL"}
              </ListItemButton>
            </>
          ) : (
            <></>
          )}
        </ListItem>
      </MList>
      {loadingFlag ? (
        <Paper sx={{ textAlign: "center" }}>
          <CircularProgress />
        </Paper>
      ) : (
        <>
          {currentCases.length === 0 ? (
            <Paper sx={{ width: "100%", textAlign: "center", height: "80%" }}>
              <Typography>No Matching Case</Typography>
            </Paper>
          ) : (
            <>
              <List
                height={height}
                width={"100%"}
                itemCount={currentCases.length}
                itemSize={170} // height of each item
                itemData={rowData}
                itemKey={(index: number, data: RowData) =>
                  data.cases[index]?._id ?? index
                }
                onItemsRendered={handleItemsRendered}
                overscanCount={4}
              >
                {MemoRow}
              </List>
              {loadingMore ? (
                <Box sx={{ textAlign: "center", padding: "8px" }}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <></>
              )}
            </>
          )}
        </>
      )}

      {enableSelection ? (
        <Box sx={{ width: "100%", textAlign: "center" }}>
          <StyledButton
            variant="contained"
            sx={{
              width: "200px",
              fontSize: "20px",
              backgroundColor: "#9E1F63FF",
              marginTop: "20px",
            }}
            onClick={handleNext}
            disabled={checkedCases.length === 0 && selectedCase._id === ""}
          >
            Next
          </StyledButton>
        </Box>
      ) : (
        <></>
      )}
    </>
  );
};

export default ThriveCaseSelection;
